import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Box, Card, Container, Grid, useTheme } from "@mui/material";
import { Alert, Button, Link, Typography } from "@platform/design-system";
import { useSignedInUser } from "@platform/user/client";
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router";

import { HelpInformation, Loader } from "../../../components";
import { ROUTE_NAMES } from "../../../constants";
import { useAuth, usePostApi } from "../../../hooks";
import { addThousandsSeparator, extractErrorMessage } from "../../../utils";
import { submitEvent } from "../../../utils/tracking";

import { AnalystDashboardRfpsTable } from "./AnalystDashboardRfpsTable";
import { DashboardGrid } from "./DashboardGrid.tsx";
// TODO(eslint) fix cycle
// eslint-disable-next-line import/no-cycle
import RfpDrafts from "./rfpDrafts";

export const MAX_DRAFT_IN_PAGE = 8;

// eslint-disable-next-line complexity
export function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const { isAuthenticated, logOut } = useAuth();

  const { givenName, familyName, isAdmin, isAnalyst, isConsultant } =
    useSignedInUser();

  const { loading, error, data, dispatchCall } = usePostApi(
    "/brokerportal/v1/prepareWelcomePage",
  );

  useEffect(() => {
    if (isAuthenticated) {
      dispatchCall({ rfpDraftCount: MAX_DRAFT_IN_PAGE + 1 });
    }
  }, [isAuthenticated, dispatchCall]);

  useEffect(() => {
    if (error?.response?.status === 401 || error?.status === 401) {
      logOut(true);
    }
  }, [error, logOut]);

  return (
    <>
      <Container>
        {error && (
          <Box pt={2}>
            <Alert severity="error">{extractErrorMessage(error)}</Alert>
          </Box>
        )}
        <Box mb={isAdmin ? 3 : 5} mt={3}>
          <DashboardGrid container gap={3}>
            <DashboardGrid size={12} mt={3} color="common.white">
              <div>
                <Typography variant="display-large" color="inherit">
                  Hi {`${givenName} ${familyName}`}!
                </Typography>
                {!isAdmin && !isAnalyst && !isConsultant && (
                  <Typography variant="display-small" color="inherit">
                    Welcome to your RFP Headquarters
                  </Typography>
                )}
                <div className="divider"></div>
              </div>
            </DashboardGrid>
            {!isAnalyst ? (
              <Grid container gap={6} size={12}>
                {(isAdmin || isConsultant) && (
                  <DashboardGrid size={2.5}>
                    <Card className="grid-card">
                      <Typography
                        variant="display-medium"
                        color="brandSecondary"
                      >
                        {loading ? (
                          <Loader isCentered={false} />
                        ) : (
                          addThousandsSeparator(data?.brokersCount) || "N/A"
                        )}
                      </Typography>
                      <Typography variant="header-large">Brokers</Typography>
                      <Link
                        component={RouterLink}
                        to={`/${ROUTE_NAMES.BROKERS}`}
                        underline="hover"
                        variant="body2"
                      >
                        VIEW ALL
                      </Link>
                    </Card>
                  </DashboardGrid>
                )}
                <DashboardGrid size={2.5}>
                  <Card className="grid-card">
                    <Typography variant="display-medium" color="brandSecondary">
                      {loading ? (
                        <Loader isCentered={false} />
                      ) : (
                        addThousandsSeparator(data?.clientsCount) || "N/A"
                      )}
                    </Typography>
                    <Typography variant="header-large">Clients</Typography>
                    <Link
                      component={RouterLink}
                      to={`/${ROUTE_NAMES.CLIENTS}`}
                      underline="hover"
                      variant="body2"
                    >
                      VIEW ALL
                    </Link>
                  </Card>
                </DashboardGrid>
                <DashboardGrid size={2.5}>
                  <Card className="grid-card">
                    <Typography variant="display-medium" color="brandSecondary">
                      {loading ? (
                        <Loader isCentered={false} />
                      ) : (
                        addThousandsSeparator(data?.rfpsCount) || "N/A"
                      )}
                    </Typography>
                    <Typography variant="header-large">RFPs</Typography>
                    <Link
                      component={RouterLink}
                      to={`/${ROUTE_NAMES.RFPS}`}
                      underline="hover"
                      variant="body2"
                    >
                      VIEW ALL
                    </Link>
                  </Card>
                </DashboardGrid>
                {!isAdmin && !isAnalyst && !isConsultant && (
                  <>
                    <DashboardGrid size={5}>
                      <Card className="grid-card">
                        <Typography variant="display-small">
                          Want to start an RFP?
                        </Typography>
                        <Button
                          variant="primary"
                          sx={{ alignSelf: "center" }}
                          style={{ marginTop: "1rem" }}
                          onClick={() => {
                            submitEvent("Start RFP");
                            navigate(`/${ROUTE_NAMES.CLIENT_SELECTION}`, {
                              state: {
                                prevPath: location.pathname,
                              },
                            });
                          }}
                        >
                          LET&apos;S GET WHAT YOU NEED TODAY
                        </Button>
                      </Card>
                    </DashboardGrid>
                    <RfpDrafts rfpDrafts={data?.rfpDrafts} loading={loading} />
                  </>
                )}
              </Grid>
            ) : (
              <AnalystDashboardRfpsTable />
            )}
          </DashboardGrid>
        </Box>
      </Container>
      <Box mb={10}>
        <Card>
          <Container>
            <DashboardGrid gap={3} justifyContent="space-between">
              <DashboardGrid size={12} className="grid-tooltip-card__inner">
                <HelpIcon
                  variant="h1"
                  sx={{
                    fontSize: "2.5rem",
                    color: theme.palette.brandSupporting.main,
                  }}
                />
                <Typography variant="body-large">
                  <strong>Need help?</strong>
                </Typography>
                <Typography variant="body-large">
                  Contact us with any questions. Please see contact information{" "}
                  <Link
                    component="button"
                    onClick={() => setIsHelpOpen(true)}
                    underline="hover"
                  >
                    <strong>here</strong>
                  </Link>
                  .
                </Typography>
              </DashboardGrid>
            </DashboardGrid>
          </Container>
        </Card>
      </Box>
      <HelpInformation isOpen={isHelpOpen} setIsOpen={setIsHelpOpen} />
    </>
  );
}
